import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Coffee, Users, Compass, HeartHandshake, Sparkles } from "lucide-react";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import { Squiggle, SquiggleUnderline, RouteLine, FloatingBlob } from "@/components/Squiggle";
import usePageMeta from "@/hooks/usePageMeta";
import { usePerspective } from "@/context/PerspectiveContext";
import { useEffect } from "react";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1502680390469-be75c86b636f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1484627147104-f5197bcd6651?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

const levelwoorden = [
  { w: "Startpunt", d: "Waar je nu staat. Zonder oordeel." },
  { w: "Rugzak", d: "Wat je meedraagt. En of dat alles wel van jou is." },
  { w: "Rem", d: "Waar je vastloopt. En waarom." },
  { w: "Vonk", d: "Waar het lichter wordt. Zelfs heel even." },
  { w: "Speelruimte", d: "Waar je mag proberen zonder dat het meteen goed moet." },
  { w: "Eerste stap", d: "Een stap die je vandaag echt kunt zetten." },
  { w: "LevelMoment", d: "Waar iets omslaat. Vaak zonder woorden." },
  { w: "Volgende beweging", d: "Wat past nu." },
];

const dag = [
  { tijd: "09:00", wat: "Aankomen · koffie · even landen", tone: "bg-ll-yellow" },
  { tijd: "09:30", wat: "Ochtend samen · check-in met de groep", tone: "bg-ll-sky" },
  { tijd: "10:30", wat: "Doen. Bewegen. Ontdekken.", tone: "bg-ll-coral text-ll-cream" },
  { tijd: "12:30", wat: "Samen eten · gewoon eten", tone: "bg-ll-yellow" },
  { tijd: "13:30", wat: "Werken aan wat jou raakt", tone: "bg-ll-sky" },
  { tijd: "15:30", wat: "Rond­sluiten · wat neem je mee?", tone: "bg-ll-cream border border-ll-ink/15" },
];

const verhalen = [
  {
    kicker: "Een moment dat we vaker horen",
    q: "Voor het eerst zei ik zelf: dit wil ik proberen.",
    foto: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    kicker: "Een moment dat we vaker horen",
    q: "Iemand wilde me niet repareren. Dat scheelde al veel.",
    foto: "https://images.unsplash.com/photo-1508979215434-fbb1c56aad33?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    kicker: "Een moment dat we vaker horen",
    q: "Twee stappen vooruit en één terug. Nog steeds één vooruit.",
    foto: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
];

const wat = [
  { icon: Coffee, t: "Rustig beginnen", d: "Geen intake vol vragen. Gewoon koffie, kennismaken, kijken of het klikt." },
  { icon: Users, t: "Een groep die je ziet", d: "Een groep die snapt hoe dit is. Zonder labels." },
  { icon: Compass, t: "Zelf richting kiezen", d: "Wij bepalen niks voor jou. We helpen kijken." },
  { icon: HeartHandshake, t: "Iemand naast je", d: "Ook op zware dagen. Een buddy, geen dossier." },
];

export default function VoorJou() {
  usePageMeta({
    title: "Voor jou · LevelLeven",
    description:
      "Waar zou jij weer zin in willen krijgen? Twaalf weken speelruimte om te ontdekken wat wél werkt.",
  });

  const { setPerspective } = usePerspective();
  useEffect(() => {
    setPerspective("deelnemer");
  }, [setPerspective]);

  return (
    <div className="bg-ll-cream text-ll-ink">
      {/* HERO */}
      <SpeelruimteBanner
        eyebrow="Voor jou"
        headline={
          <>
            Waar zou jij weer
            <br />
            zin in willen{" "}
            <span className="italic text-ll-coral relative inline-block">
              krijgen
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>?
          </>
        }
        belofte="Er hoeft nog niets vast te staan. Behalve dat er weer iets mag beginnen."
        subhead="Je hoeft niet meteen te weten wat je wilt. Je hoeft niet eerst 'klaar' te zijn. Bij LevelLeven krijg je twaalf weken speelruimte om te ontdekken wat wél werkt — in jouw tempo, met een groep die je ziet."
        chips={["12 weken START", "Vaste groep, twee trainers", "Geen intake vol vragen", "Certificaat aan het eind"]}
        ctaPrimary={{ label: "Kom een keer meelopen", to: "/contact?rol=deelnemer", testid: "voorjou-cta-meelopen" }}
        ctaSecondary={{ label: "Lees over de methode", to: "/methode" }}
        images={HERO_IMGS}
        tone="coral"
      />

      {/* WAT GA JE DOEN */}
      <section className="py-24 sm:py-32 relative">
        <FloatingBlob color="#F0B98D" size={110} className="top-10 right-10 opacity-70" />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Wat ga je doen</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Geen behandeling.{" "}
              <span className="italic text-ll-coral">Een ontdekking.</span>
            </h2>
            <p className="mt-6 text-lg text-ll-navy/80 leading-relaxed max-w-2xl">
              Je gaat niet praten óver problemen. Je gaat dóen, uitproberen, voelen wat werkt. 80% ervaring, 20% reflectie.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {wat.map((k, i) => (
              <motion.div
                key={k.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                viewport={{ once: true }}
                className="p-8 rounded-3xl bg-white border border-ll-ink/10 hover:border-ll-coral transition-colors"
              >
                <k.icon size={28} className="text-ll-coral" strokeWidth={1.5} />
                <h3 className="font-heading text-2xl mt-6 text-ll-ink font-light">{k.t}</h3>
                <p className="mt-3 text-ll-navy/80">{k.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EEN DAG BIJ LEVELLEVEN */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Zo ziet een dag eruit</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Niet zwaar.{" "}
              <span className="italic text-ll-coral">Wel echt.</span>
            </h2>
          </div>
          <div className="space-y-3 max-w-3xl">
            {dag.map((d, i) => (
              <motion.div
                key={d.tijd}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                viewport={{ once: true }}
                className={`flex items-center gap-6 p-5 rounded-2xl ${d.tone}`}
              >
                <span className="chapter-num text-2xl w-20 shrink-0">{d.tijd}</span>
                <span className="text-lg">{d.wat}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LEVELWOORDEN */}
      <section className="py-24 sm:py-32 relative">
        <FloatingBlob color="#97B79D" size={140} className="top-1/3 -left-16 opacity-70" delay={1.2} />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Onze woorden</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Woorden waar je{" "}
              <span className="italic text-ll-coral">iets aan hebt</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {levelwoorden.map((w, i) => (
              <motion.div
                key={w.w}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                viewport={{ once: true }}
                className="group"
              >
                <p className="font-heading italic text-3xl text-ll-coral group-hover:text-ll-ink transition-colors">
                  {w.w}
                </p>
                <p className="mt-2 text-ll-navy/80">{w.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VERHALEN */}
      <section className="py-24 sm:py-32 bg-ll-navy text-ll-cream">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-yellow">Momenten die we vaker horen</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Kleine dingen{" "}
              <span className="italic text-ll-coral">die iets omdraaien.</span>
            </h2>
            <p className="mt-6 text-ll-cream/70 text-sm max-w-xl italic">
              Fragmenten uit gesprekken. Geen echte namen — wel echte momenten.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {verhalen.map((v, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="aspect-[4/5] rounded-3xl overflow-hidden mb-6">
                  <img src={v.foto} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <blockquote className="font-heading italic text-2xl leading-snug text-ll-cream">
                  "{v.q}"
                </blockquote>
                <figcaption className="mt-4 text-xs uppercase tracking-[0.3em] text-ll-yellow">
                  {v.kicker}
                </figcaption>
              </motion.figure>
            ))}
          </div>
          <div className="mt-16">
            <Link
              to="/verhalen"
              className="inline-flex items-center gap-2 text-lg font-heading italic text-ll-yellow hover:text-ll-coral transition-colors"
              data-testid="voorjou-alle-verhalen"
            >
              Alle verhalen <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WAT HEB JE NODIG */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 lg:px-16 text-center relative">
          <Squiggle color="#E06B3F" className="mx-auto mb-8 w-24 h-8" duration={2.5} />
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Wat je nodig hebt</p>
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
            Geen diploma. Geen belofte.
            <br />
            <span className="italic text-ll-coral">Wel een eerlijk gesprek.</span>
          </h2>
          <p className="mt-8 text-lg text-ll-navy/80 leading-relaxed max-w-2xl mx-auto">
            Je hoeft geen doel te hebben. Je hoeft niet gemotiveerd binnen te komen. Je hoeft niet uit te leggen waarom het niet gelukt is.
            Kom een keer meelopen, kijk of het klikt, beslis daarna.
          </p>
          <div className="mt-12">
            <Link
              to="/contact?rol=deelnemer"
              data-testid="voorjou-slot-cta"
              className="group inline-flex items-center gap-4 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-all duration-300"
            >
              <span className="text-base font-medium">Ik wil een keer komen kijken</span>
              <span className="bg-ll-coral group-hover:bg-ll-yellow text-ll-ink w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                <ArrowUpRight size={18} />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
